'use client';

import * as Sentry from '@sentry/nextjs';
import { useRouter, usePathname } from 'next/navigation';
import { useLayoutEffect } from 'react';

import { logger } from '@/lib/logger/browser';
import { onUserLogin } from '@/lib/on-action';
import { getPostHog } from '@/lib/posthog-client';
import { createClient } from '@/lib/supabase/client';

/**
 * Used to indetify the users with Sentry
 * @returns
 */
const AuthHooks = () => {
  const path = usePathname();
  const router = useRouter();

  useLayoutEffect(() => {
    const supabase = createClient();

    // Tags every PostHog event with the current tenant so dashboards can be split per tenant
    const registerTenant = (user: { app_metadata?: Record<string, unknown> }) => {
      const posthog = getPostHog();
      if (!posthog) return;

      const tenantId = user.app_metadata?.tenant_id;
      if (typeof tenantId === 'string' && tenantId) {
        posthog.register({ tenant_id: tenantId });
      }
    };

    // Returns true when this is a new PostHog user (nobody or someone else was identified before)
    const identifyUser = (user: {
      id: string;
      email?: string | null;
      app_metadata?: Record<string, unknown>;
    }) => {
      // getPostHog() returns undefined when PostHog is not configured
      const posthog = getPostHog();
      if (!posthog) return false;

      const identifiedUserId = posthog.get_property('$user_id');

      if (identifiedUserId && identifiedUserId !== user.id) {
        posthog.reset();
      }

      posthog.identify(user.id, { email: user.email });

      // Done after identify, because identify may reset PostHog for a new user
      registerTenant(user);

      return identifiedUserId !== user.id;
    };

    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        Sentry.setUser({
          email: session.user.email,
          username: session.user.email ?? session.user.phone,
          id: session.user.id,
        });
        // Supabase also sends SIGNED_IN when a tab gets focus again, so only count a real change of user
        const isNewUser = identifyUser(session.user);
        if (isNewUser) {
          getPostHog()?.capture('auth_logged_in');
        }
      } else if (event === 'INITIAL_SESSION' && session) {
        Sentry.setUser({
          email: session.user.email,
          username: session.user.email ?? session.user.phone,
          id: session.user.id,
        });
        // Most logins run on the server, so the browser may first see the new user here.
        // A reload by the same user is not a new user, so nothing is sent then.
        const isNewUser = identifyUser(session.user);
        if (isNewUser) {
          getPostHog()?.capture('auth_logged_in');
        }

        onUserLogin().catch((error) => {
          Sentry.captureException(error);
          logger.error({ err: error }, 'Error during onUserLogin');
        });

        if (path.startsWith('/auth')) {
          router.push('/');
        }
      } else if ((event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && session) {
        // The tenant can change on a token refresh, for example after switching tenants
        registerTenant(session.user);
      } else if (event === 'SIGNED_OUT') {
        // Send the event before reset, so it is still linked to the user
        getPostHog()?.capture('auth_logged_out');
        getPostHog()?.reset();
      }
    });

    (async () => {
      const user = await supabase.auth.getUser();

      if (user.data.user) {
        Sentry.setUser({
          email: user.data.user.email,
          username: user.data.user.email ?? user.data.user.phone,
          id: user.data.user.id,
        });
      }
    })();
  }, [path, router]);

  return null;
};

export default AuthHooks;
